import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2,
  ShieldCheck,
  CheckCircle2,
  AlertTriangle,
  ChevronDown,
  Link as LinkIcon,
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { formatDate } from '../utils/dateFormatter';

/**
 * PolicyDeduplicateDialog — minimalist review UI (Feb 2026 rebuild).
 * -----------------------------------------------------------------
 * Design goal: understandable in two seconds. One line per duplicate
 * group ("Breach Notification Policy — 3 copies → keep 1"), with the
 * canonical version auto-picked by the server (Published preferred,
 * else latest updated_at). A "Details" disclosure lets power users
 * override the pick per group; collapsed by default.
 *
 * Nothing changes on the server until "Merge duplicates" is clicked.
 * Framework tags on merged duplicates are combined (union) — that
 * union happens server-side in /api/policies/deduplicate/execute.
 */
const PolicyDeduplicateDialog = ({ open, onOpenChange, onCompleted }) => {
  const [loading, setLoading] = useState(false);
  const [groups, setGroups] = useState([]);
  const [canonical, setCanonical] = useState({}); // key → chosen policy id (defaults to server suggestion)
  const [skipped, setSkipped] = useState({}); // key → true means "don't merge this group"
  const [excluded, setExcluded] = useState({}); // policy id → true means "keep this one separate"
  const [heal, setHeal] = useState(null);
  const [coverage, setCoverage] = useState(null);
  const [healBusy, setHealBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) {
      setGroups([]);
      setCanonical({});
      setSkipped({});
      setExcluded({});
      setHeal(null);
      setCoverage(null);
      setResult(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [dedup, healR, covR] = await Promise.all([
          axios.get('/api/policies/deduplicate/preview'),
          axios.get('/api/policies/heal/preview').catch(() => ({ data: null })),
          axios.get('/api/policies/coverage').catch(() => ({ data: null })),
        ]);
        if (cancelled) return;
        const data = dedup.data;
        setGroups(data.groups || []);
        setHeal(healR.data);
        setCoverage(covR.data);
        const picks = {};
        for (const g of data.groups || []) {
          picks[g.key] = g.suggested_canonical_id;
        }
        setCanonical(picks);
      } catch (e) {
        toast.error(e?.response?.data?.detail || 'Failed to load duplicate groups');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const totals = useMemo(() => {
    let totalDupes = 0;
    let groupsCount = 0;
    for (const g of groups) {
      if (skipped[g.key]) continue;
      const dups = g.policies.filter((p) => p.id !== canonical[g.key] && !excluded[p.id]);
      if (dups.length === 0) continue;
      groupsCount += 1;
      totalDupes += dups.length;
    }
    return { groupsCount, totalDupes, resultingCount: groupsCount };
  }, [groups, skipped, excluded, canonical]);

  const handleProceed = async () => {
    const payload = {
      groups: groups
        .filter((g) => !skipped[g.key])
        .map((g) => ({
          canonical_id: canonical[g.key],
          duplicate_ids: g.policies
            .map((p) => p.id)
            .filter((id) => id !== canonical[g.key] && !excluded[id]),
        }))
        .filter((g) => g.canonical_id && g.duplicate_ids.length > 0),
    };
    if (payload.groups.length === 0) {
      toast.info('Nothing selected to combine.');
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post('/api/policies/deduplicate/execute', payload);
      setResult(data);
      toast.success(data.summary || 'Deduplicate complete');
      if (onCompleted) onCompleted(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Deduplicate failed');
    } finally {
      setBusy(false);
    }
  };

  const applyHeal = async () => {
    setHealBusy(true);
    try {
      const { data } = await axios.post('/api/policies/heal/apply');
      toast.success(data.summary || 'Library repaired');
      const [healR, covR] = await Promise.all([
        axios.get('/api/policies/heal/preview').catch(() => ({ data: null })),
        axios.get('/api/policies/coverage').catch(() => ({ data: null })),
      ]);
      setHeal(healR.data);
      setCoverage(covR.data);
      if (onCompleted) onCompleted(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Repair failed');
    } finally {
      setHealBusy(false);
    }
  };

  const HealthPanel = () => {
    const needsRepair = heal && (heal.restored_count > 0 || heal.broken_links_found > 0);
    return (
      <div className="mb-3 rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2" data-testid="policy-health-panel">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="text-[13px] font-semibold text-slate-800 flex items-center gap-1.5">
            <ShieldCheck className="w-4 h-4 text-blue-600" /> Library health &amp; final check
          </div>
          {needsRepair && (
            <button
              type="button"
              onClick={applyHeal}
              disabled={healBusy}
              className="text-[12px] font-semibold rounded px-2.5 py-1 bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
              data-testid="policy-heal-apply-btn"
            >
              {healBusy ? 'Repairing…' : `Repair now (${heal.restored_count} restore, ${heal.broken_links_found} links)`}
            </button>
          )}
        </div>
        {heal && (
          <div className="text-[12px] text-slate-600" data-testid="policy-heal-status">
            {needsRepair ? (
              <>⚠ {heal.restored_count} topic(s) have no active policy and {heal.broken_links_found} link(s) point to an archived policy. Repair restores one active policy per topic and repoints links — nothing is deleted or archived.</>
            ) : (
              <>✓ Every topic has an active policy and no control/evidence links are broken.</>
            )}
          </div>
        )}
        {coverage && (
          <div className="text-[12px] text-slate-600" data-testid="policy-coverage-status">
            <span className="font-medium">{coverage.total_active_policies}</span> active policies ·{' '}
            <span className="font-medium">{coverage.framework_badge_sum}</span> framework badges
            {coverage.badge_sum_exceeds_total && <span className="text-emerald-700"> (multi-framework ✓)</span>}
            <div className="mt-1 flex flex-wrap gap-1.5">
              {coverage.per_framework.map((f) => (
                <span
                  key={f.framework}
                  title={f.missing_topics.length ? `Missing: ${f.missing_topics.join(', ')}` : 'Fully covered'}
                  className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10.5px] border ${f.missing_topics.length
                    ? 'bg-amber-50 text-amber-800 border-amber-200'
                    : 'bg-emerald-50 text-emerald-800 border-emerald-200'}`}
                >
                  {f.framework}: {f.covered_topics}/{f.required_topics}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) onOpenChange(v); }}>
      <DialogContent
        className="max-w-2xl w-[92vw] max-h-[85vh] overflow-y-auto"
        data-testid="policy-deduplicate-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-blue-600" />
            {result ? 'Deduplicate summary' : 'Merge duplicate policies'}
          </DialogTitle>
          <DialogDescription>
            {result
              ? result.summary
              : 'One policy is kept per group and tagged with all its frameworks. The rest are moved to Archived (never deleted), with control & evidence links repointed to the kept policy. Nothing changes until you confirm.'}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-12 flex items-center justify-center text-slate-500 text-sm">
            <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Scanning for duplicates…
          </div>
        ) : result ? (
          <div className="py-4" data-testid="policy-deduplicate-summary">
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
              <div className="font-semibold flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" />
                Kept {result.resulting_policy_count} policies and archived {result.merged_duplicate_count} duplicates.
              </div>
              <p className="mt-1 text-[12.5px] text-emerald-800/90">
                Framework tags combined onto the kept policy; control & evidence links repointed; duplicates moved to Archived (recoverable).
              </p>
            </div>
          </div>
        ) : groups.length === 0 ? (
          <div className="py-2">
            <HealthPanel />
            <div className="py-8 text-center text-slate-500 text-sm">
              <CheckCircle2 className="w-10 h-10 mx-auto mb-3 text-emerald-500" />
              No duplicate policies found in this workspace.
            </div>
          </div>
        ) : (
          <div className="py-2">
            <HealthPanel />
            <ul className="divide-y divide-slate-100" data-testid="policy-deduplicate-group-list">
            {groups.map((g) => (
              <GroupRow
                key={g.key}
                group={g}
                canonicalId={canonical[g.key]}
                onCanonicalChange={(pid) => setCanonical((p) => ({ ...p, [g.key]: pid }))}
                skipped={!!skipped[g.key]}
                onToggleSkip={() => setSkipped((s) => ({ ...s, [g.key]: !s[g.key] }))}
                excluded={excluded}
                onToggleExclude={(pid) => setExcluded((e) => ({ ...e, [pid]: !e[pid] }))}
              />
            ))}
          </ul>
          </div>
        )}

        {!result && !loading && groups.length > 0 && (
          <p className="text-[12px] text-slate-500 flex items-center gap-1.5">
            <ShieldCheck className="w-3.5 h-3.5 text-slate-400" />
            Nothing changes until you confirm. The kept policy gets all frameworks; duplicates move to Archived (never deleted).
          </p>
        )}

        <DialogFooter className="pt-2 border-t border-slate-100">
          {result ? (
            <Button onClick={() => onOpenChange(false)} data-testid="policy-deduplicate-close-btn">
              Done
            </Button>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={busy}
                data-testid="policy-deduplicate-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={handleProceed}
                disabled={busy || loading || totals.totalDupes === 0}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="policy-deduplicate-proceed-btn"
              >
                {busy ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Merging…
                  </>
                ) : (
                  `Combine & archive duplicates${totals.totalDupes ? ` (${totals.totalDupes})` : ''}`
                )}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---- Minimal group row --------------------------------------------------
// One line: "Name — N copies → keep 1". Details disclosure (closed by
// default) reveals the per-version list so power users can override the
// auto-pick.
const GroupRow = ({ group, canonicalId, onCanonicalChange, skipped, onToggleSkip, excluded, onToggleExclude }) => {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const chosen = (group.policies || []).find((p) => p.id === canonicalId);
  const chosenVer = chosen?.version;
  const chosenStatus = (chosen?.status || 'draft').toLowerCase();
  const mergeCount = (group.policies || []).filter((p) => p.id !== canonicalId && !excluded[p.id]).length;

  return (
    <li
      className={`py-3 ${skipped ? 'opacity-50' : ''}`}
      data-testid={`policy-deduplicate-group-${group.key.replace(/\s+/g, '-')}`}
    >
      <div className="flex items-baseline gap-2 text-[14px] text-slate-900">
        <span className={`font-semibold truncate ${skipped ? 'line-through' : ''}`} title={group.display_name}>
          {group.display_name}
        </span>
        <span className="text-slate-500">
          — {group.policies.length} similar → {skipped ? 'skipped' : `keep 1, archive ${mergeCount}`}
        </span>
        {group.content_differs && !skipped && (
          <span
            className="inline-flex items-center gap-1 text-[10.5px] font-semibold text-amber-800 bg-amber-100 border border-amber-200 rounded px-1.5 py-0.5"
            title="Duplicates have different content — review Details before merging"
          >
            <AlertTriangle className="w-3 h-3" /> content differs
          </span>
        )}
        <button
          type="button"
          onClick={onToggleSkip}
          className={`ml-auto shrink-0 text-[11px] font-semibold rounded px-2 py-0.5 border ${skipped
            ? 'text-blue-700 border-blue-200 bg-blue-50 hover:bg-blue-100'
            : 'text-slate-500 border-slate-200 hover:bg-slate-100'}`}
          data-testid={`policy-deduplicate-skip-${group.key.replace(/\s+/g, '-')}`}
        >
          {skipped ? 'Include group' : "Don't combine"}
        </button>
      </div>
      {!skipped && (<>
      <div className="mt-0.5 flex items-center gap-2 text-[12px] text-slate-500">
        <span>
          keeping <span className="font-medium text-slate-700">v{chosenVer ?? '—'}</span>
          {chosenStatus === 'published' && (
            <span className="ml-1 text-emerald-700 font-medium">· published</span>
          )}
        </span>
        <span aria-hidden>·</span>
        <button
          type="button"
          onClick={() => setDetailsOpen((v) => !v)}
          className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline"
          aria-expanded={detailsOpen}
          data-testid={`policy-deduplicate-details-toggle-${group.key.replace(/\s+/g, '-')}`}
        >
          Details
          <ChevronDown
            className={`w-3.5 h-3.5 transition-transform ${detailsOpen ? 'rotate-180' : ''}`}
          />
        </button>
      </div>

      {detailsOpen && (
        <div
          className="mt-2 ml-1 rounded-md border border-slate-200 bg-slate-50/60"
          data-testid={`policy-deduplicate-details-${group.key.replace(/\s+/g, '-')}`}
        >
          <ul className="divide-y divide-slate-100">
            {group.policies.map((p) => (
              <VersionRow
                key={p.id}
                policy={p}
                groupKey={group.key}
                isCanonical={p.id === canonicalId}
                onChoose={() => onCanonicalChange(p.id)}
                isExcluded={!!excluded[p.id]}
                onToggleExclude={() => onToggleExclude(p.id)}
              />
            ))}
          </ul>
          {(group.combined_frameworks || []).length > 0 && (
            <div className="px-3 py-2 border-t border-slate-100 flex flex-wrap items-center gap-1 text-[11px] text-slate-600">
              <span className="font-medium mr-1">Frameworks after merge:</span>
              {group.combined_frameworks.map((f) => (
                <Badge
                  key={f}
                  variant="outline"
                  className="px-1.5 py-0 text-[10px] font-semibold border-slate-300 text-slate-700 bg-white"
                >
                  {f}
                </Badge>
              ))}
            </div>
          )}
        </div>
      )}
      </>)}
    </li>
  );
};

const VersionRow = ({ policy: p, groupKey, isCanonical, onChoose, isExcluded, onToggleExclude }) => {
  const s = (p.status || 'draft').toLowerCase();
  const pill =
    s === 'published'
      ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
      : s === 'archived'
        ? 'bg-slate-100 text-slate-700 border-slate-200'
        : 'bg-amber-100 text-amber-800 border-amber-200';
  return (
    <li className={`px-3 py-2 flex items-start gap-2.5 ${isCanonical ? 'bg-blue-50/40' : ''} ${isExcluded ? 'opacity-45' : ''}`}>
      <input
        type="radio"
        name={`canon-${groupKey}`}
        className="mt-1 w-3.5 h-3.5 accent-blue-600"
        checked={isCanonical}
        onChange={onChoose}
        disabled={isExcluded}
        aria-label={`Keep ${p.name} v${p.version}`}
        data-testid={`policy-deduplicate-radio-${p.id}`}
      />
      <div className="flex-1 min-w-0 text-[12.5px]">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className={`font-semibold text-slate-800 ${isExcluded ? 'line-through' : ''}`} title={p.name}>{p.name}</span>
          <span className="text-slate-400">v{p.version}</span>
          <span className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${pill}`}>
            {s}
          </span>
          {isCanonical && (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-blue-800 bg-blue-100 border border-blue-200 rounded px-1.5 py-0.5">
              <CheckCircle2 className="w-3 h-3" /> Keep
            </span>
          )}
          {isExcluded && (
            <span className="text-[10px] font-semibold text-slate-600 bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5">
              keep separate
            </span>
          )}
          {!isCanonical && (
            <button
              type="button"
              onClick={onToggleExclude}
              className="ml-auto shrink-0 text-[11px] font-medium text-slate-500 hover:text-slate-800 hover:underline"
              data-testid={`policy-deduplicate-exclude-${p.id}`}
            >
              {isExcluded ? 'Add back' : 'Not a duplicate'}
            </button>
          )}
        </div>
        <div className="mt-0.5 text-[11.5px] text-slate-500">
          Updated {formatDate(p.updated_at)} · {p.acceptance_count} acceptance{p.acceptance_count === 1 ? '' : 's'} ·{' '}
          {(p.linked_controls || []).length} linked control
          {(p.linked_controls || []).length === 1 ? '' : 's'}
          {(p.linked_controls || []).length > 0 && (
            <>
              {' '}
              <LinkIcon className="inline w-3 h-3 -mt-0.5 opacity-60" />
            </>
          )}
        </div>
      </div>
    </li>
  );
};

export default PolicyDeduplicateDialog;
